import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface LearnerProfile {
  id: string;
  fullName: string | null;
  email: string | null;
  avatarUrl: string | null;
  role: string | null;
}

export interface CurriculumEnrollment {
  id: string;
  curriculumId: string;
  curriculumTitle: string;
  curriculumDescription: string | null;
  enrolledAt: string;
  completedAt: string | null;
  isCompleted: boolean;
  progressPercentage: number;
  totalCourses: number;
  completedCourses: number;
}

export interface CourseEnrollment {
  id: string;
  courseId: string;
  courseTitle: string;
  courseDescription: string | null;
  curriculumTitle: string | null;
  enrolledAt: string;
  completedAt: string | null;
  isCompleted: boolean;
  progressPercentage: number;
  totalModules: number;
  completedModules: number;
}

export interface LearnerAssignment {
  id: string;
  type: "curriculum" | "course";
  itemId: string;
  itemTitle: string;
  assignedAt: string;
  assignedBy: string | null;
  assignedByName: string | null;
  dueDate: string | null;
  acknowledgedAt: string | null;
  completedAt: string | null;
  required: boolean;
  notes: string | null;
}

export interface LearnerCertificate {
  id: string;
  curriculumId: string;
  curriculumTitle: string;
  certificateNumber: string;
  issuedAt: string;
  userName: string;
}

export interface LearnerProgressSummary {
  totalCurriculumEnrollments: number;
  completedCurriculumEnrollments: number;
  inProgressCurriculumEnrollments: number;
  totalCourseEnrollments: number;
  completedCourseEnrollments: number;
  inProgressCourseEnrollments: number;
  averageCurriculumProgress: number;
  averageCourseProgress: number;
  totalAssignments: number;
  completedAssignments: number;
  pendingAssignments: number;
  certificatesEarned: number;
}

export interface LearnerProgressData {
  profile: LearnerProfile | null;
  curriculumEnrollments: CurriculumEnrollment[];
  courseEnrollments: CourseEnrollment[];
  assignments: LearnerAssignment[];
  certificates: LearnerCertificate[];
  summary: LearnerProgressSummary;
}

export function useLearnerProgress(learnerId: string | null) {
  return useQuery({
    queryKey: ["learner-progress", learnerId],
    queryFn: async (): Promise<LearnerProgressData> => {
      if (!learnerId) {
        return getEmptyData();
      }

      // Fetch all data in parallel
      const [
        profileResult,
        emailResult,
        curriculumEnrollmentsResult,
        courseEnrollmentsResult,
        curriculumAssignmentsResult,
        courseAssignmentsResult,
        certificatesResult,
        moduleProgressResult,
      ] = await Promise.all([
        // Profile data - direct query by ID
        supabase
          .from("profiles")
          .select("id, full_name, avatar_url, role")
          .eq("id", learnerId)
          .single(),
        // Email from profile_private
        supabase
          .from("profile_private")
          .select("email")
          .eq("user_id", learnerId)
          .single(),
        // Curriculum enrollments
        supabase
          .from("curriculum_enrollments")
          .select(`
            id,
            curriculum_id,
            enrolled_at,
            completed_at,
            curricula:curriculum_id (
              id,
              title,
              description
            )
          `)
          .eq("user_id", learnerId),
        // Course enrollments
        supabase
          .from("course_enrollments")
          .select(`
            id,
            course_id,
            enrolled_at,
            completed_at,
            courses:course_id (
              id,
              title,
              description,
              curriculum_id,
              curricula:curriculum_id (
                title
              )
            )
          `)
          .eq("user_id", learnerId),
        // Curriculum assignments
        supabase
          .from("curriculum_assignments")
          .select(`
            id,
            curriculum_id,
            assigned_at,
            assigned_by,
            due_date,
            acknowledged_at,
            completed_at,
            required,
            notes,
            curricula:curriculum_id (
              title
            ),
            assigner:assigned_by (
              full_name
            )
          `)
          .eq("user_id", learnerId),
        // Course assignments
        supabase
          .from("course_assignments")
          .select(`
            id,
            course_id,
            assigned_at,
            assigned_by,
            due_date,
            acknowledged_at,
            completed_at,
            required,
            notes,
            courses:course_id (
              title
            ),
            assigner:assigned_by (
              full_name
            )
          `)
          .eq("user_id", learnerId),
        // Certificates
        supabase
          .from("certificates")
          .select("id, curriculum_id, curriculum_title, certificate_number, issued_at, user_name")
          .eq("user_id", learnerId),
        // Module progress for calculating course progress
        supabase
          .from("module_progress")
          .select(`
            id,
            module_id,
            enrollment_id,
            completed_at
          `),
      ]);

      // Build profile from direct query results
      const learnerProfile: LearnerProfile | null = profileResult.data
        ? {
            id: profileResult.data.id,
            fullName: profileResult.data.full_name,
            email: emailResult.data?.email || null,
            avatarUrl: profileResult.data.avatar_url,
            role: profileResult.data.role,
          }
        : null;

      // Get curriculum courses for progress calculation
      const { data: curriculumCourses } = await supabase
        .from("curriculum_courses")
        .select("curriculum_id, course_id");

      // Get modules for course progress calculation
      const { data: allModules } = await supabase.from("modules").select("id, course_id");

      // Process curriculum enrollments
      const curriculumEnrollments: CurriculumEnrollment[] = (
        curriculumEnrollmentsResult.data || []
      ).map((enrollment) => {
        const curriculum = enrollment.curricula as { id: string; title: string; description: string | null } | null;
        
        // Count courses in this curriculum
        const coursesInCurriculum =
          curriculumCourses?.filter((cc) => cc.curriculum_id === enrollment.curriculum_id) || [];
        const totalCourses = coursesInCurriculum.length;

        // Count completed courses for this user
        const completedCourses = (courseEnrollmentsResult.data || []).filter(
          (ce) =>
            ce.completed_at &&
            coursesInCurriculum.some((cc) => cc.course_id === ce.course_id)
        ).length;

        const progressPercentage = totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0;

        return {
          id: enrollment.id,
          curriculumId: enrollment.curriculum_id,
          curriculumTitle: curriculum?.title || "Unknown Curriculum",
          curriculumDescription: curriculum?.description || null,
          enrolledAt: enrollment.enrolled_at,
          completedAt: enrollment.completed_at,
          isCompleted: !!enrollment.completed_at,
          progressPercentage,
          totalCourses,
          completedCourses,
        };
      });

      // Process course enrollments
      const courseEnrollments: CourseEnrollment[] = (courseEnrollmentsResult.data || []).map(
        (enrollment) => {
          const course = enrollment.courses as {
            id: string;
            title: string;
            description: string | null;
            curriculum_id: string | null;
            curricula: { title: string } | null;
          } | null;

          // Count modules in this course
          const modulesInCourse = allModules?.filter((m) => m.course_id === enrollment.course_id) || [];
          const totalModules = modulesInCourse.length;

          // Count completed modules for this enrollment
          const completedModules = (moduleProgressResult.data || []).filter(
            (mp) =>
              mp.completed_at &&
              mp.enrollment_id === enrollment.id &&
              modulesInCourse.some((m) => m.id === mp.module_id)
          ).length;

          const progressPercentage = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

          return {
            id: enrollment.id,
            courseId: enrollment.course_id,
            courseTitle: course?.title || "Unknown Course",
            courseDescription: course?.description || null,
            curriculumTitle: course?.curricula?.title || null,
            enrolledAt: enrollment.enrolled_at,
            completedAt: enrollment.completed_at,
            isCompleted: !!enrollment.completed_at,
            progressPercentage,
            totalModules,
            completedModules,
          };
        }
      );

      // Process assignments
      const assignments: LearnerAssignment[] = [
        ...(curriculumAssignmentsResult.data || []).map((a) => {
          const curriculum = a.curricula as { title: string } | null;
          const assigner = a.assigner as { full_name: string } | null;
          return {
            id: a.id,
            type: "curriculum" as const,
            itemId: a.curriculum_id,
            itemTitle: curriculum?.title || "Unknown Curriculum",
            assignedAt: a.assigned_at,
            assignedBy: a.assigned_by,
            assignedByName: assigner?.full_name || null,
            dueDate: a.due_date,
            acknowledgedAt: a.acknowledged_at,
            completedAt: a.completed_at,
            required: a.required,
            notes: a.notes,
          };
        }),
        ...(courseAssignmentsResult.data || []).map((a) => {
          const course = a.courses as { title: string } | null;
          const assigner = a.assigner as { full_name: string } | null;
          return {
            id: a.id,
            type: "course" as const,
            itemId: a.course_id,
            itemTitle: course?.title || "Unknown Course",
            assignedAt: a.assigned_at,
            assignedBy: a.assigned_by,
            assignedByName: assigner?.full_name || null,
            dueDate: a.due_date,
            acknowledgedAt: a.acknowledged_at,
            completedAt: a.completed_at,
            required: a.required,
            notes: a.notes,
          };
        }),
      ].sort((a, b) => new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime());

      // Process certificates
      const certificates: LearnerCertificate[] = (certificatesResult.data || []).map((c) => ({
        id: c.id,
        curriculumId: c.curriculum_id,
        curriculumTitle: c.curriculum_title,
        certificateNumber: c.certificate_number,
        issuedAt: c.issued_at,
        userName: c.user_name,
      }));

      // Calculate summary stats
      const completedCurriculumEnrollments = curriculumEnrollments.filter((e) => e.isCompleted).length;
      const inProgressCurriculumEnrollments = curriculumEnrollments.length - completedCurriculumEnrollments;
      const completedCourseEnrollments = courseEnrollments.filter((e) => e.isCompleted).length;
      const inProgressCourseEnrollments = courseEnrollments.length - completedCourseEnrollments;

      const averageCurriculumProgress =
        curriculumEnrollments.length > 0
          ? Math.round(
              curriculumEnrollments.reduce((sum, e) => sum + e.progressPercentage, 0) /
                curriculumEnrollments.length
            )
          : 0;

      const averageCourseProgress =
        courseEnrollments.length > 0
          ? Math.round(
              courseEnrollments.reduce((sum, e) => sum + e.progressPercentage, 0) /
                courseEnrollments.length
            )
          : 0;

      const completedAssignments = assignments.filter((a) => a.completedAt).length;
      const pendingAssignments = assignments.length - completedAssignments;

      const summary: LearnerProgressSummary = {
        totalCurriculumEnrollments: curriculumEnrollments.length,
        completedCurriculumEnrollments,
        inProgressCurriculumEnrollments,
        totalCourseEnrollments: courseEnrollments.length,
        completedCourseEnrollments,
        inProgressCourseEnrollments,
        averageCurriculumProgress,
        averageCourseProgress,
        totalAssignments: assignments.length,
        completedAssignments,
        pendingAssignments,
        certificatesEarned: certificates.length,
      };

      return {
        profile: learnerProfile,
        curriculumEnrollments,
        courseEnrollments,
        assignments,
        certificates,
        summary,
      };
    },
    enabled: !!learnerId,
  });
}

function getEmptyData(): LearnerProgressData {
  return {
    profile: null,
    curriculumEnrollments: [],
    courseEnrollments: [],
    assignments: [],
    certificates: [],
    summary: {
      totalCurriculumEnrollments: 0,
      completedCurriculumEnrollments: 0,
      inProgressCurriculumEnrollments: 0,
      totalCourseEnrollments: 0,
      completedCourseEnrollments: 0,
      inProgressCourseEnrollments: 0,
      averageCurriculumProgress: 0,
      averageCourseProgress: 0,
      totalAssignments: 0,
      completedAssignments: 0,
      pendingAssignments: 0,
      certificatesEarned: 0,
    },
  };
}
